import { supabase } from '../lib/supabase';
import { getCenterIdByDomain as getCenterIdByDomainFromVideos } from './videos';
import config from '../config';

/**
 * Service for fetching video data from Supabase
 */
export class VideosService {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Get center ID by domain
   */
  async getCenterIdByDomain(domain) {
    const cacheKey = `center_${domain}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      // Use the more robust implementation from videos.js
      const centerId = await getCenterIdByDomainFromVideos(domain);
      
      if (!centerId) {
        console.error('No center found for domain:', domain);
        return null;
      }

      // Get center details
      const { data, error } = await supabase
        .from('centers')
        .select('id, name')
        .eq('id', centerId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching center details:', error);
        return null;
      }

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error in getCenterIdByDomain:', error);
      return null;
    }
  }

  /**
   * Fetch videos for a center
   * @param {string} centerId - Center ID
   * @param {string} videoType - 'video' or 'live'
   * @param {number} page - Page number (0-based)
   * @param {number} limit - Number of videos per page
   * @returns {Promise<Object>} - Videos data with pagination info
   */
  async fetchVideos(centerId, videoType = 'video', page = 0, limit = 20) {
    console.log('[VideosService] fetchVideos called with:', { centerId, videoType, page, limit });
    
    try {
      const offset = page * limit;
      
      let query = supabase
        .from('videos')
        .select(`
          *,
          video_analytics (
            view_count,
            like_count,
            total_play_count
          )
        `)
        .eq('center_id', centerId)
        .eq('privacy_status', 'public');

      // Filter by video type
      if (videoType === 'live') {
        query = query.eq('video_type', 'live');
      } else {
        // Based on the existing videos.js, regular videos are stored as 'regular' type
        query = query.eq('video_type', 'regular');
      }

      // Special ordering for "All" category as per PRD requirements
      // Latest 5, then most liked 5, then remaining by published_at DESC
      const { data, error } = await query
        .order('published_at', { ascending: false })
        .range(offset, offset + limit - 1);

      console.log('[VideosService] Query result:', { 
        dataLength: data?.length || 0, 
        error,
        firstVideo: data?.[0] 
      });

      if (error) {
        console.error('Error fetching videos:', error);
        return { videos: [], hasMore: false, total: 0 };
      }

      // Transform the data to match component expectations
      const transformedVideos = data.map((video, index) => ({
        id: video.youtube_video_id,
        title: video.title,
        thumbnail: video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_video_id}/maxresdefault.jpg`,
        duration: this.formatDuration(video.duration),
        views: this.formatViews(video.youtube_view_count || video.video_analytics?.view_count || 0),
        published: this.formatPublishedDate(video.published_at),
        video_type: video.video_type,
        youtube_video_id: video.youtube_video_id,
        video_url: video.video_url,
        publishedAt: video.published_at,
        isLiveNow: video.is_live_now,
        youtube_view_count: video.youtube_view_count || 0,
        view_count: video.youtube_view_count || video.video_analytics?.view_count || 0,
        like_count: video.video_analytics?.like_count || 0,
        total_play_count: video.video_analytics?.total_play_count || 0,
        published_at: video.published_at,
        orderIndex: offset + index
      }));

      return {
        videos: transformedVideos,
        hasMore: data.length === limit,
        total: data.length
      };
    } catch (error) {
      console.error('Error in fetchVideos:', error);
      return { videos: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Format video duration from seconds to MM:SS format
   */
  formatDuration(seconds) {
    if (!seconds) return '0:00';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Format view count (e.g., 1200 -> 1.2K)
   */
  formatViews(views) {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    } else if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K`;
    } else {
      return views.toString();
    }
  }

  /**
   * Format published date (e.g., "2 days ago")
   */
  formatPublishedDate(publishedAt) {
    if (!publishedAt) return 'Unknown';
    
    try {
      const published = new Date(publishedAt);
      
      // Use Intl.DateTimeFormat for consistent date formatting
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      };
      
      return new Intl.DateTimeFormat('en-US', options).format(published);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown';
    }
  }

  /**
   * Get video by ID
   */
  async getVideoById(videoId) {
    try {
      // Check if videoId is a UUID (database ID) or YouTube video ID
      // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(videoId);
      
      console.log('[VideosService] getVideoById:', { videoId, isUUID });
      
      // Query by the appropriate field
      const queryField = isUUID ? 'id' : 'youtube_video_id';
      
      const { data, error } = await supabase
        .from('videos')
        .select(`
          *,
          video_analytics (
            view_count,
            like_count,
            total_play_count
          )
        `)
        .eq(queryField, videoId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching video:', error);
        return null;
      }

      // Add view_count and like_count to the main video object for compatibility
      // Prefer YouTube view count over in-app view count for display
      if (data) {
        data.view_count = data.youtube_view_count || data.video_analytics?.view_count || 0;
        data.like_count = data.video_analytics?.like_count || 0;
        data.total_play_count = data.video_analytics?.total_play_count || 0;
      }

      return data;
    } catch (error) {
      console.error('Error in getVideoById:', error);
      return null;
    }
  }

  /**
   * Extract Google Sheet ID from URL
   */
  extractSheetId(input) {
    if (!input) return '';
    if (!input.includes('http')) return input;
    
    const match = input.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return match && match[1] ? match[1] : input;
  }

  /**
   * Fetch video metadata from Google Sheets
   */
  async fetchVideoMetadataFromSheets(googleSheetId) {
    if (!googleSheetId) {
      console.log('No Google Sheet ID provided');
      return [];
    }

    const sheetId = this.extractSheetId(googleSheetId);
    const range = 'Sheet1!A:Z'; // Adjust range as needed
    const apiKey = config.GOOGLE_API_KEY;

    if (!apiKey) {
      console.error('Google API key not found in config');
      return [];
    }

    try {
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${apiKey}`;
      console.log('Fetching video metadata from Google Sheets:', sheetId);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch Google Sheets data: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.values || data.values.length < 2) {
        console.log('No video data found in Google Sheets');
        return [];
      }
      
      const headers = data.values[0].map(h => h.toLowerCase().trim());
      const rows = data.values.slice(1);
      
      // Find column indices
      const findColumnIndex = (possibleNames) => {
        for (const name of possibleNames) {
          const index = headers.indexOf(name.toLowerCase());
          if (index !== -1) return index;
        }
        return -1;
      };
      
      const videoUrlIndex = findColumnIndex(['FullVideoURL', 'fullvideourl', 'full video', 'full_video', 'video url', 'video_url', 'youtube url']);
      const titleIndex = findColumnIndex(['title', 'video title', 'lecture title']);
      const eventIndex = findColumnIndex(['Event', 'event']);
      const lectureSeriesIdIndex = findColumnIndex(['Lecture Series ID', 'lecture_series_id', 'series_id', 'Series ID']);
      const speakerIndex = findColumnIndex(['speaker', 'speakerName', 'speaker_name', 'sheikh', 'Speaker']);
      const dateIndex = findColumnIndex(['publishedAt', 'published_at', 'date', 'publish date', 'upload date']);
      
      console.log('Video metadata column mapping:', {
        videoUrl: videoUrlIndex,
        title: titleIndex,
        event: eventIndex,
        lectureSeriesId: lectureSeriesIdIndex,
        speaker: speakerIndex,
        date: dateIndex
      });
      
      // Process rows
      const processedVideos = rows
        .map((row, index) => {
          const videoUrl = row[videoUrlIndex]?.trim();
          if (!videoUrl || (!videoUrl.includes('youtube.com') && !videoUrl.includes('youtu.be'))) return null;
          
          // Extract video ID from URL (exclude tracking parameters)
          const videoIdMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n\r\/\?]+)/);
          const videoId = videoIdMatch ? videoIdMatch[1] : null;
          
          return {
            youtube_video_id: videoId,
            title: row[titleIndex]?.trim() || `Video ${index + 1}`,
            // Use Event (human-readable) for series display, not lectureSeriesId (technical identifier)
            series: row[eventIndex]?.trim() || 'Uncategorized',
            eventName: row[eventIndex]?.trim() || 'Uncategorized',
            speakerName: row[speakerIndex]?.trim() || 'Speaker',
            publishedAt: row[dateIndex]?.trim() || null,
            videoUrl: videoUrl
          };
        })
        .filter(Boolean); // Remove null entries
      
      console.log(`Processed ${processedVideos.length} videos from Google Sheets`);
      
      // Debug: Log unique series found
      const uniqueSeries = [...new Set(processedVideos.map(v => v.series))];
      console.log('Unique series found in videos:', uniqueSeries);
      
      // Debug: Log some Q&A videos if found
      const qaVideos = processedVideos.filter(v => 
        v.title?.toLowerCase().includes('q&a') || 
        v.title?.toLowerCase().includes('question') ||
        v.eventName?.toLowerCase().includes('q&a')
      );
      console.log(`Found ${qaVideos.length} Q&A videos:`, qaVideos.slice(0, 3));
      
      // Debug: Show example of cleaned video IDs
      const sampleVideos = processedVideos.slice(0, 5);
      console.log('Sample video ID extraction:', sampleVideos.map(v => ({
        url: v.videoUrl,
        extractedId: v.youtube_video_id
      })));
      
      return processedVideos;
      
    } catch (error) {
      console.error('Error fetching video metadata from Google Sheets:', error);
      return [];
    }
  }

  /**
   * Get series data from Google Sheets (for series categories)
   */
  async getSeriesCategories(googleSheetId) {
    // If no sheet ID provided, return default
    if (!googleSheetId) {
      return [{ 
        name: 'All', 
        id: 'all',
        eventName: 'All Videos' 
      }];
    }

    try {
      // Fetch video metadata from Google Sheets
      const videoMetadata = await this.fetchVideoMetadataFromSheets(googleSheetId);
      
      if (videoMetadata.length === 0) {
        return [{ 
          name: 'All', 
          id: 'all',
          eventName: 'All Videos' 
        }];
      }
      
      // Extract unique series with their event names
      const seriesMap = new Map();
      
      videoMetadata.forEach(video => {
        const seriesId = video.series;
        const eventName = video.eventName;
        
        if (seriesId && seriesId !== 'all' && seriesId !== 'All') {
          // Store the event name for this series ID
          if (!seriesMap.has(seriesId)) {
            seriesMap.set(seriesId, {
              name: eventName || seriesId,
              id: seriesId,
              eventName: eventName || seriesId,
              latestDate: video.publishedAt ? new Date(video.publishedAt) : new Date(0)
            });
          } else {
            // Update latest date if this video is newer
            const existing = seriesMap.get(seriesId);
            const videoDate = video.publishedAt ? new Date(video.publishedAt) : new Date(0);
            if (videoDate > existing.latestDate) {
              existing.latestDate = videoDate;
            }
          }
        }
      });
      
      // Convert map to array and sort by latest date
      const categories = [
        { 
          name: 'All', 
          id: 'all',
          eventName: 'All Videos' 
        },
        ...Array.from(seriesMap.values())
          .sort((a, b) => b.latestDate - a.latestDate)
          .map(({ name, id, eventName }) => ({ name, id, eventName }))
      ];
      
      console.log('Extracted categories from Google Sheets:', categories);
      return categories;
      
    } catch (error) {
      console.error('Error getting series categories:', error);
      // Return default on error
      return [{ 
        name: 'All', 
        id: 'all',
        eventName: 'All Videos' 
      }];
    }
  }

  /**
   * Clear cache (useful for testing or when data changes)
   */
  clearCache() {
    this.cache.clear();
  }
}

// Export singleton instance
export const videosService = new VideosService();